import {
  DealType,
  CardFilter,
  CardFilterPrice,
  CardFilterPriceSchema,
  Deal,
} from '@ca/common/types'
import { router, useGlobalSearchParams } from 'expo-router'
import { useIsNavigationReady } from 'hooks/useIsNavigationReady'
import { getRemoteConfig } from 'hooks/useRemoteConfigStore'
import { usePastDeals } from 'hooks/usePastDeals'
import { useRef, useCallback, useEffect } from 'react'
import ConfigConditions from 'features/config/ConfigConditions'

export type CardFilters = {
  tag?: string
  type?: DealType
  tags_excluded?: string
  price?: CardFilterPrice
}

export type PersistedParams = CardFilters & {
  past_deals?: string
}

// HACK: We need to handle the case where the value is 'undefined'
// https://github.com/expo/expo/pull/34950
const getValue = <T extends string>(value: T | undefined) => {
  if (!value) return undefined
  if (value === 'undefined') return undefined
  return value
}

const getValues = <T extends string>(value: T | undefined) => {
  if (!value) return undefined
  if (value === 'undefined') return undefined
  return value.split(',')
}

const getDealTag = (deal: Deal, pastDeals: boolean) => {
  const config = getRemoteConfig()
  if (!config || !config.tags) return undefined

  const settings = ConfigConditions.value(
    pastDeals ? 'intro:all.tags' : 'intro:tags',
    'tags',
    config
  )

  return deal.tags?.find((t) => settings?.tags?.includes(t))
}

export const getParamsFromDeal = (deal?: Deal | null) => {
  if (!deal) return undefined
  const isPastDeal = !deal.tags?.includes('active')
  return {
    tag: getDealTag(deal, isPastDeal),
    type: deal.type ?? undefined,
    past_deals: isPastDeal ? '1' : undefined,
  }
}

export const useFilterParams = () => {
  const { tag, type, past_deals, tags_excluded, price } =
    useGlobalSearchParams<PersistedParams>()

  return {
    tag: getValue(tag),
    type: getValue(type),
    past_deals: getValue(past_deals),
    tags_excluded: getValues(tags_excluded),
    price: CardFilterPriceSchema.safeParse(getValue(price)).data,
  }
}

export const useCardFilter = (config?: {
  initial?: CardFilters & { past_deals?: boolean }
}) => {
  const params = useFilterParams()
  const isNavReady = useIsNavigationReady()
  const isInitialSet = useRef(false)

  const pastDeals = usePastDeals(config?.initial?.past_deals)

  const setTag = useCallback((tag: string | undefined) => {
    router.setParams({ tag: tag ?? undefined })
  }, [])

  const setType = useCallback((type: DealType | undefined) => {
    router.setParams({ type: type ?? undefined })
  }, [])

  const setTagsExcluded = useCallback((tagsExcluded: string[] | undefined) => {
    router.setParams({ tags_excluded: tagsExcluded?.join(',') ?? undefined })
  }, [])

  const setPrice = useCallback((price: CardFilterPrice | undefined) => {
    router.setParams({ price: price ?? undefined })
  }, [])

  const setCardFilter = (filter: CardFilter) => {
    setTag(filter.tag ?? '')
    setType(filter.type ?? undefined)
    setTagsExcluded(filter.tagsExcluded ?? undefined)
    setPrice(filter.price ?? undefined)
  }

  useEffect(() => {
    if (!isNavReady || !config?.initial) return
    if (!params.tag && config.initial.tag && !isInitialSet.current) {
      setTag(config.initial.tag)
    }
    if (!params.type && config.initial.type && !isInitialSet.current) {
      setType(config.initial.type)
    }
    if (
      !params.tags_excluded &&
      config.initial.tags_excluded &&
      !isInitialSet.current
    ) {
      setTagsExcluded(getValues(config.initial.tags_excluded))
    }
    if (!params.price && config.initial.price && !isInitialSet.current) {
      setPrice(config.initial.price)
    }
    isInitialSet.current = true
  }, [isNavReady, config?.initial])

  return {
    tag: params.tag ?? config?.initial?.tag,
    setTag,
    type: params.type ?? config?.initial?.type,
    setType,
    setCardFilter,
    pastDeals,
    params,
    setTagsExcluded,
    setPrice,
    price: params.price ?? config?.initial?.price,
    tagsExcluded:
      params.tags_excluded ?? getValues(config?.initial?.tags_excluded),
  }
}
